/* eslint-disable tsdoc/syntax */
import { memo, useCallback, useEffect, useRef, useState } from "react";

import {
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";

import Close from "@zenith/assets/icons/Close";
import {
  MajorButtonComponent,
  OtpCountdownComponent,
  SelectComponent,
  PinInputComponent,
  PinRulesComponent,
  SpinnerComponent,
  TextFieldComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import {
  PIN_MISMATCH_MESSAGE,
  REPEATED_REGEX,
  SEQUENTIAL_REGEX,
  TRANSACTIONS_PIN_LENGTH,
  PIN_CREATED_SUCCESSFULLY_TITLE,
  PIN_CREATED_SUCCESSFULLY_MESSAGE,
  PIN_CREATED_FAILED_TITLE,
  PIN_CREATED_FAILED_MESSAGE,
  SUCCESS_API_CODE,
  DEFAULT_CHANNEL,
  DEFAULT_PAGE_SIZE,
  SECTOR_SUB_CATEGORIES_LIMIT,
} from "@zenith/data/constants";
import {
  setConfirmTransactionPinValidations,
  setTransactionPinValidations,
} from "@zenith/features/Authentication/validations/AuthValidations";
import { encrypt } from "@zenith/utils";
import {
  ApiResponseDTOEncryptionDTO,
  ApiResponseDTOTransactionPinDetailDTO,
  BusinessApiService,
  BusinessSectorUpdateRequestDTO,
  SendOtpDTO,
  TransactionPinDTO,
  UsersApiService,
  UtilityApiService,
  VerifyOtpDTO,
  WalletApiService,
} from "@zenith/services/user";
import type { BusinessSubCategorySuggestionDTO } from "@zenith/services/user/models/BusinessSubCategorySuggestionDTO";
import { UpdateMerchantEmailRequestDTO } from "@zenith/services/user/models/UpdateMerchantEmailRequestDTO";
import {
  useRegistrationContext,
  useUserDetailsContext,
  useNotificationContext,
} from "@zenith/context";
import SearchIcon from "@zenith/assets/icons/SearchIcon";
import TransparentCheck from "@zenith/assets/icons/TransparentCheck";
import { BusinessSector } from "@zenith/types";

const OTP_EXPIRY_SECONDS = 60;

const CompleteRegistrationModal = memo(function CompleteRegistrationModal() {
  const xApiKey = import.meta.env.VITE_X_API_KEY;

  const { registrationContext, setRegistrationContext } =
    useRegistrationContext();
  const { isRegistrationCompleted, isBusinessSectorCompleted } =
    registrationContext;
  const { userDetailsContext, setUserDetailsContext } = useUserDetailsContext();
  const { setOpen } = useNotificationContext();

  const [isSequential, setIsSequential] = useState(true);
  const [isRepeated, setIsRepeated] = useState(true);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");

  const [showEmailVerification, setShowEmailVerification] =
    useState<boolean>(false);
  const [isRegistrationModalOpen, setisRegistrationModalOpen] =
    useState<boolean>(false);
  const [showBusinessSector, setShowBusinessSector] = useState<boolean>(false);
  const [showSetTransactonPin, setShowTransactonPin] = useState<boolean>(false);
  const [showAddEmail, setShowAddEmail] = useState<boolean>(false);
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [emailAddressError, setEmailAddressError] = useState<string>("");
  const [emailOtp, setEmailOtp] = useState<Array<number | string>>(
    new Array(6).fill("")
  );
  const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
  const [isResendLoading, setIsResendLoading] = useState<boolean>(false);
  const [emailApiError, setEmailApiError] = useState<string>("");
  const [otpApiError, setOtpApiError] = useState<string>("");

  useEffect(() => {
    if (userDetailsContext.isLoadingUser) return;
    const needsBusinessSector = !isBusinessSectorCompleted;
    const needsPin = isBusinessSectorCompleted && !isRegistrationCompleted;
    const needsEmail =
      isBusinessSectorCompleted &&
      isRegistrationCompleted &&
      !userDetailsContext.email;

    setShowBusinessSector(needsBusinessSector);
    setShowTransactonPin(needsPin);
    setShowAddEmail(needsEmail);

    setisRegistrationModalOpen(needsBusinessSector || needsPin || needsEmail);
  }, [isBusinessSectorCompleted, isRegistrationCompleted, userDetailsContext.email, userDetailsContext.isLoadingUser]);

  const [otpSeconds, setOtpSeconds] = useState<number>(OTP_EXPIRY_SECONDS);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startOtpTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setOtpSeconds(OTP_EXPIRY_SECONDS);
    timerRef.current = setInterval(() => {
      setOtpSeconds((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (showEmailVerification) startOtpTimer();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [showEmailVerification, startOtpTimer]);
  const [businessSectors, setBusinessSectors] = useState<BusinessSector[]>(
    []
  );
  const [isLoadingBusinessSectors, setIsLoadingBusinessSectors] =
    useState<boolean>(false);
  const [selectedSectorId, setSelectedSectorId] = useState<string>("");
  const [subCategories, setSubCategories] = useState<
    BusinessSubCategorySuggestionDTO[]
  >([]);
  const [isLoadingSubCategories, setIsLoadingSubCategories] =
    useState<boolean>(false);
  const [selectedSubCategoryId, setSelectedSubCategoryId] =
    useState<string>("");
  const [suggestions, setSuggestions] = useState<
    BusinessSubCategorySuggestionDTO[]
  >([]);
  const [isSearchingSuggestions, setIsSearchingSuggestions] =
    useState<boolean>(false);
  const [isLoadingProceed, setIsLoadingProceed] = useState<boolean>(false);

  const [formData, setFormData] = useState<{
    transactionPin: string;
    confirmTransactionPin: string;
  }>({
    transactionPin: "",
    confirmTransactionPin: "",
  });

  const [transactionPin, setTransactionPin] = useState<Array<number | string>>(
    new Array(TRANSACTIONS_PIN_LENGTH).fill("")
  );
  const [confirmTransactionPin, setConfirmTransactionPin] = useState<
    Array<number | string>
  >(new Array(TRANSACTIONS_PIN_LENGTH).fill(""));

  const [transactionPinError, setTransactionPinError] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: "",
  });

  const [confirmTransactionPinError, setConfirmTransactionPinError] = useState<{
    hasError: boolean;
    message: string;
  }>({
    hasError: false,
    message: "",
  });

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ) {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  const onPinChanged = (pinEntry: number | string, index: number) => {
    const pin = [...transactionPin];
    pin[index] = pinEntry;
    setTransactionPin(pin);
    setTransactionPinError({ message: "", hasError: false });
    const pinStr = pin.join("");
    setFormData({ ...formData, transactionPin: pinStr });
    updatePinState(pinStr);
    if (
      pinStr.length === TRANSACTIONS_PIN_LENGTH &&
      formData.confirmTransactionPin.length === TRANSACTIONS_PIN_LENGTH
    ) {
      setConfirmTransactionPinError(
        pinStr !== formData.confirmTransactionPin
          ? { hasError: true, message: PIN_MISMATCH_MESSAGE }
          : { hasError: false, message: "" }
      );
    }
  };

  const onPinBlur = (): void => {
    setTransactionPinError(
      setTransactionPinValidations(formData.transactionPin)
    );

    // Check confirm pin if pin was changed after confirm pin is inputed
    if (
      formData.transactionPin.length === TRANSACTIONS_PIN_LENGTH &&
      formData.confirmTransactionPin.length === TRANSACTIONS_PIN_LENGTH
    ) {
      setConfirmTransactionPinError(
        setTransactionPinValidations(
          formData.confirmTransactionPin,
          formData.transactionPin
        )
      );
    }
  };

  const onConfirmPinChanged = (pinEntry: number | string, index: number) => {
    const confirmPin = [...confirmTransactionPin];
    confirmPin[index] = pinEntry;
    setConfirmTransactionPin(confirmPin);
    const confirmPinStr = confirmPin.join("");
    setFormData({ ...formData, confirmTransactionPin: confirmPinStr });
    if (
      confirmPinStr.length === TRANSACTIONS_PIN_LENGTH &&
      formData.transactionPin.length === TRANSACTIONS_PIN_LENGTH
    ) {
      setConfirmTransactionPinError(
        confirmPinStr !== formData.transactionPin
          ? { hasError: true, message: PIN_MISMATCH_MESSAGE }
          : { hasError: false, message: "" }
      );
    } else {
      setConfirmTransactionPinError({ message: "", hasError: false });
    }
  };

  const onConfirmPinBlur = (): void => {
    if (
      formData.transactionPin.length === TRANSACTIONS_PIN_LENGTH &&
      formData.confirmTransactionPin.length === TRANSACTIONS_PIN_LENGTH
    ) {
      setConfirmTransactionPinError(
        setTransactionPinValidations(
          formData.confirmTransactionPin,
          formData.transactionPin
        )
      );
    } else {
      setConfirmTransactionPinError(
        setConfirmTransactionPinValidations(formData.confirmTransactionPin)
      );
    }
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSuggestions([]);
  };

  const updatePinState = (pin: string) => {
    if (pin.length === TRANSACTIONS_PIN_LENGTH) {
      const sequential = SEQUENTIAL_REGEX.test(pin);
      const repeated = REPEATED_REGEX.test(pin);

      setIsSequential(sequential);
      setIsRepeated(repeated);
    } else {
      setIsSequential(true);
      setIsRepeated(true);
    }
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (formData.transactionPin !== formData.confirmTransactionPin) {
      setConfirmTransactionPinError({
        hasError: true,
        message: PIN_MISMATCH_MESSAGE,
      });
      return;
    }

    setLoading(true);
    UtilityApiService.getPublicKey(DEFAULT_CHANNEL, xApiKey)
      .then((response: ApiResponseDTOEncryptionDTO) => {
        if ("data" in response && response.data) {
          const publicKeyBase64 = response.data.publicKeyBase64;

          const encryptedTransactionPin = encrypt(
            formData.transactionPin,
            publicKeyBase64 || ""
          );

          const encryptedConfirmTransactionPin = encrypt(
            formData.confirmTransactionPin,
            publicKeyBase64 || ""
          );

          const request: TransactionPinDTO = {
            transactionPin: encryptedTransactionPin as string,
            newTransactionPin: encryptedConfirmTransactionPin,
          };

          WalletApiService.createTransactionPin(request)
            .then((response: ApiResponseDTOTransactionPinDetailDTO) => {
              if (response.success) {
                setRegistrationContext({
                  isRegistrationCompleted,
                  isBusinessSectorCompleted: true,
                });
                setUserDetailsContext({
                  ...userDetailsContext,
                });
                notify(
                  PIN_CREATED_SUCCESSFULLY_TITLE,
                  PIN_CREATED_SUCCESSFULLY_MESSAGE,
                  "success"
                );
                setShowTransactonPin(false);
                setShowAddEmail(true);
              } else {
                notify(
                  PIN_CREATED_FAILED_TITLE,
                  PIN_CREATED_FAILED_MESSAGE,
                  "error"
                );
              }
            })
            .catch((error) => {
              notify(
                PIN_CREATED_FAILED_TITLE,
                PIN_CREATED_FAILED_MESSAGE,
                "error"
              );
              throw new Error(error);
            })
            .finally(() => setLoading(false));
        }
      })
      .catch((error) => {
        setLoading(false);
        notify(PIN_CREATED_FAILED_TITLE, PIN_CREATED_FAILED_MESSAGE, "error");
        throw new Error(error);
      });
  };

  const handleRegistrationModal = () => {
    if (showBusinessSector) return;
    setisRegistrationModalOpen(false);
  };

  const selectedSector = businessSectors.find(
    (sector) => String(sector.sectorId) === selectedSectorId
  );
  const selectedSubCategory = subCategories.find(
    (subCategory) => String(subCategory.subCategoryId) === selectedSubCategoryId
  );

  useEffect(() => {
    if (!showBusinessSector) return;

    setIsLoadingBusinessSectors(true);
    BusinessApiService.getBusinessSectors(1, 500)
      .then((response) => {
        setBusinessSectors(
          (response.data?.businessSectors ?? []).map((sector) => ({
            sectorId: sector.sectorId,
            name: sector.name,
            mccCode: sector.mccCode,
          }))
        );
      })
      .catch((error) => {
        console.error("Error fetching business sectors:", error);
      })
      .finally(() => setIsLoadingBusinessSectors(false));
  }, [showBusinessSector]);

  useEffect(() => {
    const query = searchText.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    let isCurrent = true;
    const debounce = setTimeout(() => {
      setIsSearchingSuggestions(true);
      BusinessApiService.searchBusinessSubCategories(
        query,
        DEFAULT_PAGE_SIZE
      )
        .then((response) => {
          if (isCurrent) setSuggestions(response.data?.suggestions ?? []);
        })
        .catch((error) => {
          console.error("Error searching business sub-categories:", error);
          if (isCurrent) setSuggestions([]);
        })
        .finally(() => {
          if (isCurrent) setIsSearchingSuggestions(false);
        });
    }, 300);

    return () => {
      isCurrent = false;
      clearTimeout(debounce);
    };
  }, [searchText]);

  useEffect(() => {
    if (!selectedSector?.name) {
      setSubCategories([]);
      return;
    }

    let isCurrent = true;
    setIsLoadingSubCategories(true);
    BusinessApiService.searchBusinessSubCategories(
      selectedSector.name,
      SECTOR_SUB_CATEGORIES_LIMIT
    )
      .then((response) => {
        if (!isCurrent) return;
        setSubCategories(
          (response.data?.suggestions ?? []).filter(
            (suggestion) =>
              suggestion.sectorId === selectedSector.sectorId &&
              suggestion.subCategoryId !== undefined
          )
        );
      })
      .catch((error) => {
        console.error("Error fetching sector sub-categories:", error);
        if (isCurrent) setSubCategories([]);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingSubCategories(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [selectedSector?.sectorId, selectedSector?.name]);

  const handleSectorChange = (sectorId: string | null) => {
    setSelectedSectorId(sectorId ?? "");
    setSelectedSubCategoryId("");
  };

  const handleSuggestionClick = (
    suggestion: BusinessSubCategorySuggestionDTO
  ) => {
    if (suggestion.sectorId === undefined) return;

    setBusinessSectors((current) =>
      current.some((sector) => sector.sectorId === suggestion.sectorId)
        ? current
        : [
            ...current,
            {
              sectorId: suggestion.sectorId,
              name: suggestion.sectorName,
              mccCode: suggestion.mccCode,
            },
          ]
    );
    setSelectedSectorId(String(suggestion.sectorId));
    setSelectedSubCategoryId(
      suggestion.subCategoryId !== undefined
        ? String(suggestion.subCategoryId)
        : ""
    );
    setSearchText("");
    setSuggestions([]);
  };

  const handleProceed = async () => {
    try {
      setIsLoadingProceed(true);
      if (selectedSector?.sectorId !== undefined) {
        const businessSectorUpdateRequest: BusinessSectorUpdateRequestDTO = {
          sectorId: selectedSector.sectorId,
          name: selectedSector.name ?? "",
          mccCode: selectedSubCategory?.mccCode || selectedSector.mccCode || "",
          subCategoryId: selectedSubCategory?.subCategoryId,
        };

        const response = await BusinessApiService.updateBusinessSector(
          businessSectorUpdateRequest
        );

        if (response.success && response.internalCode === SUCCESS_API_CODE) {
          setRegistrationContext({
            isRegistrationCompleted,
            isBusinessSectorCompleted: true,
          });
          notify(
            "Success",
            response.data?.message || "Business sector saved successfully",
            "success"
          );
          if (isRegistrationCompleted) {
            setisRegistrationModalOpen(false);
          } else {
            setShowBusinessSector(false);
            setShowTransactonPin(true);
          }
        }
      }
    } catch (error: any) {
      console.error(error);
      const errorMessage = error.body?.message || error.message;
      notify(error, errorMessage, "error");
    } finally {
      setIsLoadingProceed(false);
    }
  };

  const maskEmail = (email: string): string => {
    const [local, domain] = email.split("@");
    if (!domain) return email;
    const maskedLocal =
      local.length > 4 ? local.slice(0, 3) + "******" + local.slice(-2) : "***";
    const dotIndex = domain.lastIndexOf(".");
    const domainName = domain.slice(0, dotIndex);
    const tld = domain.slice(dotIndex);
    const maskedDomain =
      domainName.length > 1 ? domainName[0] + "********" : domainName;
    return `${maskedLocal}@${maskedDomain}${tld}`;
  };

  const handleEmailSubmit = async (isResend = false) => {
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!EMAIL_REGEX.test(emailAddress.trim())) {
      setEmailAddressError("Please enter a valid email address");
      return;
    }
    setEmailApiError("");
    try {
      isResend ? setIsResendLoading(true) : setIsEmailLoading(true);
      const requestBody: SendOtpDTO = {
        channel: SendOtpDTO.channel.WEB,
        platform: SendOtpDTO.platform.EMAIL,
        purpose: SendOtpDTO.purpose.EMAIL_SETUP,
        email: emailAddress.trim(),
        phoneNumber: userDetailsContext.phoneNumber,
        countryCode: SendOtpDTO.countryCode.NG,
      };
      const response = await UtilityApiService.sendOtp(requestBody);
      if (response.success) {
        if (isResend) {
          setEmailOtp(new Array(6).fill(""));
          startOtpTimer();
          notify("Success", "OTP sent successfully", "success");
        } else {
          setShowAddEmail(false);
          setShowEmailVerification(true);
        }
      } else {
        const msg = response.message ?? "Failed to send OTP";
        if (isResend) {
          notify("Error", msg, "error");
        } else {
          setEmailApiError(msg);
        }
      }
    } catch (error) {
      const err = error as { body?: { message?: string } };
      const msg = err?.body?.message ?? "Failed to send OTP";
      if (isResend) {
        notify("Error", msg, "error");
      } else {
        setEmailApiError(msg);
      }
    } finally {
      isResend ? setIsResendLoading(false) : setIsEmailLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpApiError("");
    try {
      setIsEmailLoading(true);
      const verifyResponse = await UtilityApiService.verifyOtp({
        channel: VerifyOtpDTO.channel.WEB,
        email: emailAddress.trim(),
        purpose: "EMAIL_SETUP" as VerifyOtpDTO.purpose,
        verificationCode: emailOtp.join(""),
        phoneNumber: userDetailsContext.phoneNumber,
        countryCode: VerifyOtpDTO.countryCode.NG,
      });
      if (verifyResponse.success && verifyResponse.data?.xMfa) {
        const updateResponse = await UsersApiService.updateEmail(
          verifyResponse.data.xMfa,
          { email: emailAddress.trim() } as UpdateMerchantEmailRequestDTO
        );
        if (updateResponse.success) {
          notify("Success", "Email added successfully", "success");
          setUserDetailsContext({
            email: emailAddress.trim(),
            isLoadingUser: false,
          });
          setRegistrationContext({
            isRegistrationCompleted: true,
            isBusinessSectorCompleted: true,
          });
          setisRegistrationModalOpen(false);
          UsersApiService.getUser().then((res) => {
            if (res.data) {
              setUserDetailsContext({
                email: res.data.email ?? "",
                isLoadingUser: false,
              });
            }
          });
        } else {
          setOtpApiError(updateResponse.message ?? "Failed to update email");
        }
      } else {
        setOtpApiError(
          verifyResponse.message ?? "Invalid OTP. Please try again."
        );
      }
    } catch (error) {
      const err = error as { body?: { message?: string } };
      setOtpApiError(err?.body?.message ?? "Invalid OTP. Please try again.");
    } finally {
      setIsEmailLoading(false);
      setUserDetailsContext({
        isLoadingUser: false,
      });
    }
  };

  const isCompleteRegistrationDisabled =
    formData.transactionPin.length !== TRANSACTIONS_PIN_LENGTH ||
    formData.confirmTransactionPin.length !== TRANSACTIONS_PIN_LENGTH ||
    formData.transactionPin !== formData.confirmTransactionPin ||
    transactionPinError.hasError ||
    confirmTransactionPinError.hasError ||
    isRepeated ||
    isSequential;

  const isProceedButtonDisabled =
    !selectedSector ||
    !selectedSubCategory ||
    isLoadingSubCategories ||
    isLoadingProceed ||
    isLoadingBusinessSectors;

  return (
    <div>
      {/* Toast Snackbar for notifications */}
      {(toasterData.title || toasterData.message) && (
        <ToastSnackbarComponent
          horizontal="center"
          status={toasterData.status}
          vertical="top"
          hasAutoHideDuration
          size="lg"
          title={toasterData.title}
          text={toasterData.message}
        />
      )}
      <Modal
        open={isRegistrationModalOpen}
        aria-labelledby="complete-registration-modal-title"
        onClose={(_, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            handleRegistrationModal();
          }
        }}
        aria-describedby="complete-registration-modal-description"
      >
        <Box className="absolute top-1/2 left-1/2 flex max-h-[95vh] w-full -translate-x-1/2 -translate-y-1/2 transform flex-col overflow-hidden rounded-xl bg-white outline-none md:w-3/4 lg:w-120">
          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
            <div className="flex w-full flex-col px-8 pt-8 pb-2">
              <LinearProgress
                className="w-full rounded-md"
                variant="determinate"
                value={
                  showBusinessSector
                    ? 25
                    : showSetTransactonPin
                    ? 50
                    : showAddEmail
                    ? 100
                    : 100
                }
                color="error"
              />
              {showBusinessSector && (
                <div>
                  <div className="my-3 flex flex-col items-center justify-center text-center">
                    <p className="w-full text-xl font-semibold lg:w-1/2">
                      Add your Business Sector
                    </p>
                    <p className="w-full text-center text-sm text-neutral-600 lg:w-1/2">
                      Select the Business Sector that closely aligns with your
                      line of business.
                    </p>
                  </div>
                  <div className="relative">
                    <TextFieldComponent
                      id="business-sector-search-field"
                      label=""
                      labelClassName="hidden"
                      containerClassName="[&>div]:w-full [&>div]:h-10"
                      labelId="business-search-field-label"
                      placeholder="Search keywords"
                      className=" border border-neutral-200 bg-transparent px-4 py-1 text-sm shadow-none [&.text-field]:rounded-lg [&>input]:placeholder:text-xs [&>input]:placeholder:font-normal [&>input]:placeholder:text-neutral-200"
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      hasError={false}
                      validationMessage={""}
                      maxLength={50}
                      endDecorator={
                        <>
                          <div className="ml-6 mr-3 h-4/5 border-r border-[#D1D4DB]"></div>
                          <IconButton className="border-black-500 mr-0.5 border-l-8 hover:bg-transparent">
                            {searchText.length > 0 ? (
                              <IconButton
                                onClick={handleClearSearch}
                                aria-label="Clear search"
                              >
                                <Close className="text-neutral-400" />
                              </IconButton>
                            ) : (
                              <SearchIcon />
                            )}
                          </IconButton>
                        </>
                      }
                    />
                    {searchText.trim().length >= 2 && (
                      <div className="absolute inset-x-0 top-full z-10 mt-2 flex max-h-72 flex-col gap-y-1.5 overflow-y-auto rounded-xl bg-white p-2.5 shadow-[0px_4px_24px_rgba(0,0,0,0.08)]">
                        {isSearchingSuggestions ? (
                          <div className="flex justify-center py-3">
                            <SpinnerComponent type="primary" size={24} />
                          </div>
                        ) : suggestions.length > 0 ? (
                          suggestions.map((suggestion) => (
                            <button
                              type="button"
                              key={`${suggestion.sectorId}-${suggestion.subCategoryId}`}
                              className="w-full rounded-md bg-neutral-25 px-3 py-3 text-left text-sm text-neutral-600 hover:bg-neutral-50"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              {suggestion.subCategoryName ??
                                suggestion.sectorName}
                            </button>
                          ))
                        ) : (
                          <p className="px-3 py-3 text-sm text-neutral-600">
                            No Business Sector found
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-6 flex flex-col gap-y-5">
                    <SelectComponent
                      id="business-sector-select"
                      listboxZIndex={1300}
                      labelId="business-sector-select-label"
                      label="Business Sector"
                      placeholder="Select Option"
                      className="w-auto"
                      items={businessSectors
                        .filter((sector) => sector.sectorId !== undefined)
                        .map((sector) => ({
                          value: String(sector.sectorId),
                          label: sector.name ?? "",
                        }))}
                      value={selectedSectorId}
                      onChange={(_, value) => handleSectorChange(value)}
                      disabled={isLoadingBusinessSectors}
                    />
                    {(subCategories.length > 0 || !selectedSector) && (
                      <SelectComponent
                        id="business-sub-category-select"
                        listboxZIndex={1300}
                        labelId="business-sub-category-select-label"
                        label="Sector Sub-Category"
                        placeholder="Select Option"
                        className="w-auto"
                        items={subCategories.map((subCategory) => ({
                          value: String(subCategory.subCategoryId),
                          label: subCategory.subCategoryName ?? "",
                        }))}
                        value={selectedSubCategoryId}
                        onChange={(_, value) =>
                          setSelectedSubCategoryId(value ?? "")
                        }
                        disabled={!selectedSector || isLoadingSubCategories}
                      />
                    )}
                  </div>
                  <div className="mt-6 mb-3 flex justify-start">
                    <MajorButtonComponent
                      type="button"
                      size="large"
                      buttonType="primary"
                      id="proceed-button"
                      className={`bg-primary-150 w-full rounded-lg text-base font-medium normal-case text-white`}
                      onClick={handleProceed}
                      disabled={isProceedButtonDisabled}
                      isLoading={isLoadingProceed}
                    >
                      Proceed
                    </MajorButtonComponent>
                  </div>
                </div>
              )}
            </div>
            {showSetTransactonPin && (
              <>
                <div className="flex w-full flex-col px-8 py-6">
                  <div className="mb-2 flex flex-row">
                    <h1 className="align-center flex text-2xl font-semibold">
                      Complete Your Registration
                    </h1>
                  </div>

                  <div className="flex flex-col">
                    <p className="text-sm text-neutral-600">
                      Enjoy full access to all app features!
                    </p>
                    <p className="text-sm text-neutral-600">
                      Complete your profile information and create your
                      transaction PIN to get started
                    </p>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="no-scrollbar h-auto overflow-y-auto px-8 pt-3 md:pt-6">
                    <div className="mt-3 mb-2 lg:mb-8 lg:mt-12">
                      <h2 className="mb-4">Transaction PIN</h2>
                      <p className="text-sm text-neutral-600">
                        Setup your transaction PIN. This will be used to
                        authorise your debit transactions.
                      </p>
                    </div>

                    <div className="mb-5 max-w-[18rem]">
                      <PinInputComponent
                        id="registration-transaction-pin"
                        label="Create four (4) digit PIN"
                        labelClassName="mb-1 text-neutral-600"
                        pinContainerClass="gap-2 md:gap-4 xl:gap-6"
                        pinLength={TRANSACTIONS_PIN_LENGTH}
                        pin={transactionPin}
                        onPinChanged={onPinChanged}
                        onBlur={() => {
                          onPinBlur();
                        }}
                        hasError={transactionPinError?.hasError}
                        type="tel"
                        masked={true}
                        isValidating={false}
                        validationMessage={transactionPinError?.message}
                      />
                    </div>

                    <div className="mb-8 max-w-[18rem]">
                      <PinInputComponent
                        id="registration-transaction-confirm-pin"
                        label="Confirm four (4) digit PIN"
                        labelClassName="mb-1 text-neutral-600"
                        pinContainerClass="gap-2 md:gap-4 xl:gap-6"
                        pinLength={TRANSACTIONS_PIN_LENGTH}
                        pin={confirmTransactionPin}
                        onPinChanged={onConfirmPinChanged}
                        onBlur={() => {
                          onConfirmPinBlur();
                        }}
                        masked={true}
                        hasError={confirmTransactionPinError?.hasError}
                        type="tel"
                        isValidating={false}
                        validationMessage={confirmTransactionPinError?.message}
                      />
                    </div>

                    <PinRulesComponent
                      className="pb-6"
                      isRepeated={isRepeated}
                      isSequential={isSequential}
                      necessaryPinLength={TRANSACTIONS_PIN_LENGTH}
                      pinLength={formData.transactionPin.length}
                    />
                  </div>

                  <div className="flex justify-start gap-2 px-8 py-6 md:gap-4">
                    <Button
                      id="complete-registration-button"
                      className="w-full rounded-lg bg-primary-300 py-3 px-2 text-xs font-medium normal-case text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-neutral-50 md:px-8 md:text-base"
                      size="large"
                      type="submit"
                      disabled={isCompleteRegistrationDisabled || loading}
                    >
                      Proceed{" "}
                      {loading ? (
                        <CircularProgress
                          className="ml-2 text-primary-100"
                          size={"26px"}
                        />
                      ) : (
                        ""
                      )}
                    </Button>
                  </div>
                </form>
              </>
            )}

            {showAddEmail && (
              <div className="flex w-full flex-col px-8 py-2">
                <h1 className="text-[32px] font-medium">
                  Complete your registration
                </h1>
                <h2 className="mb-4 mt-8 text-3xl font-medium">Add Email</h2>
                <p className="mb-4 text-base text-neutral-600">
                  Add your email address to receive notifications and alerts.
                </p>
                <TextFieldComponent
                  id="email-address-field"
                  label="Email Address"
                  labelId="email-address-label"
                  placeholder="Enter your email address"
                  type="email"
                  value={emailAddress}
                  onChange={(e) => {
                    const value = e.target.value;
                    setEmailAddress(value);
                    setEmailApiError("");
                    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (value && !EMAIL_REGEX.test(value)) {
                      setEmailAddressError(
                        "Please enter a valid email address"
                      );
                    } else {
                      setEmailAddressError("");
                    }
                  }}
                  hasError={!!emailAddressError || !!emailApiError}
                  validationMessage={emailAddressError}
                />
                {emailApiError && (
                  <p className="mt-1 text-sm text-red-600">{emailApiError}</p>
                )}
                <div className="my-8 flex justify-start gap-4">
                  <Button
                    id="submit-email-button"
                    className="w-full rounded-lg bg-primary-300 py-3 px-2 text-xs font-medium normal-case text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-neutral-50 md:px-8 md:text-base"
                    size="large"
                    disabled={
                      !emailAddress.trim() ||
                      !!emailAddressError ||
                      isEmailLoading
                    }
                    onClick={() => handleEmailSubmit(false)}
                  >
                    {isEmailLoading ? (
                      <CircularProgress
                        size={20}
                        className="text-primary-100"
                      />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </div>
            )}

            {showEmailVerification && (
              <div className="flex w-full flex-col px-8 py-6">
                <h1 className="text-[32px] font-medium">
                  Complete your registration
                </h1>
                <h2 className="mb-4 mt-8 text-3xl font-medium">
                  Email Verification
                </h2>
                <div className="mb-6 flex items-start gap-3 rounded-lg px-2">
                  <TransparentCheck
                    className="text-primary-150 mt-0.5 shrink-0"
                    fontSize="small"
                  />
                  <p className="text-sm text-neutral-600">
                    The OTP has been sent to your email{" "}
                    <span className="font-medium text-neutral-800">
                      {maskEmail(emailAddress)}
                    </span>
                  </p>
                </div>
                <div className="mb-0 w-[70%]">
                  <PinInputComponent
                    id="email-otp-input"
                    label="Enter code here"
                    labelClassName="mb-2 text-sm text-neutral-600"
                    pinContainerClass="gap-3"
                    inputClassName="!h-[56px]"
                    pinLength={6}
                    pin={emailOtp}
                    onPinChanged={(pinEntry, index) => {
                      const otp = [...emailOtp];
                      otp[index] = pinEntry;
                      setEmailOtp(otp);
                      setOtpApiError("");
                    }}
                    onBlur={() => undefined}
                    hasError={!!otpApiError}
                    type="tel"
                    masked={true}
                    isValidating={false}
                    validationMessage=""
                  />
                  {otpApiError && (
                    <p className="mt-2 text-sm text-red-600">{otpApiError}</p>
                  )}
                </div>
                <div className="mb-14 mt-4 text-base font-normal text-neutral-500">
                  {otpSeconds > 0 ? (
                    <OtpCountdownComponent seconds={otpSeconds} />
                  ) : (
                    <span>
                      Didn&apos;t receive an OTP?{" "}
                      <button
                        type="button"
                        className="cursor-pointer border-0 bg-transparent p-0 font-normal text-neutral-900 underline disabled:cursor-not-allowed disabled:opacity-50"
                        onClick={() => handleEmailSubmit(true)}
                        disabled={isResendLoading}
                      >
                        {isResendLoading ? "Sending..." : "Resend OTP"}
                      </button>
                    </span>
                  )}
                </div>
                <div className="mb-4 flex justify-between gap-4">
                  <Button
                    id="email-otp-back-button"
                    className="w-1/2 rounded-lg border-1 border-solid border-neutral-300 py-3 px-2 text-xs font-medium normal-case text-neutral-600 md:px-8 md:text-base"
                    size="large"
                    onClick={() => {
                      setShowEmailVerification(false);
                      setShowAddEmail(true);
                      setEmailOtp(new Array(6).fill(""));
                      setOtpApiError("");
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    id="email-otp-continue-button"
                    className="w-1/2 rounded-lg bg-primary-300 py-3 px-2 text-xs font-medium normal-case text-white hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-neutral-50 md:px-8 md:text-base"
                    size="large"
                    disabled={
                      !emailOtp.every((d) => d !== "" && d !== undefined) ||
                      isEmailLoading
                    }
                    onClick={handleVerifyOtp}
                  >
                    {isEmailLoading ? (
                      <CircularProgress
                        className="text-primary-100"
                        size="20px"
                      />
                    ) : (
                      "Continue"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Box>
      </Modal>
    </div>
  );
});

export default CompleteRegistrationModal;
